import { useState, useCallback, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { cn } from '@/lib/utils';
import { getPropertyImage } from '@/lib/formatters';

interface PropertyImageCarouselProps {
  images: string[] | null;
  alt: string;
  showDots?: boolean;
  showArrows?: boolean;
  className?: string;
  priority?: boolean;
}

export function PropertyImageCarousel({
  images,
  alt,
  showDots = true,
  showArrows = true,
  className,
  priority = false,
}: PropertyImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  // Track which indices have been detected as tiny/icon images
  const skippedIndices = useRef(new Set<number>());

  const imageList = images && images.length > 0 ? images : [getPropertyImage(null)];
  const hasMultipleImages = imageList.length > 1;

  // Preload adjacent images so next/prev clicks feel instant
  useEffect(() => {
    if (!hasMultipleImages) return;
    // Only preload next image (not 3) to save bandwidth on mobile
    const nextIdx = (currentIndex + 1) % imageList.length;
    const img = new Image();
    const url = imageList[nextIdx];
    img.src = url.includes('ddfcdn.realtor.ca') ? url.replace('/highres/', '/medres/') : url;
  }, [currentIndex, imageList, hasMultipleImages]);

  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => setIsAnimating(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);

  // When a tiny/icon image is detected, skip to the next real image
  const handleTinyImage = useCallback(() => {
    if (!hasMultipleImages) return;

    skippedIndices.current.add(currentIndex);

    // Find the next non-skipped image
    for (let offset = 1; offset < imageList.length; offset++) {
      const nextIdx = (currentIndex + offset) % imageList.length;
      if (!skippedIndices.current.has(nextIdx)) {
        setCurrentIndex(nextIdx);
        return;
      }
    }
    // All images are tiny — stay on current (better than nothing)
  }, [currentIndex, hasMultipleImages, imageList.length]);

  const goToNext = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % imageList.length);
  }, [imageList.length]);

  const goToPrevious = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + imageList.length) % imageList.length);
  }, [imageList.length]);

  const goToIndex = useCallback((index: number, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (index !== currentIndex) {
      setIsAnimating(true);
      setCurrentIndex(index);
    }
  }, [currentIndex]);

  // Swipe handlers
  const minSwipeDistance = 30;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && hasMultipleImages) goToNext();
    if (isRightSwipe && hasMultipleImages) goToPrevious();
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <div 
      className={cn("relative w-full h-full touch-pan-y", className)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{ contain: 'layout style paint' }}
    >
      <div className="w-full h-full overflow-hidden bg-muted">
        <OptimizedImage
          src={imageList[currentIndex]}
          alt={`${alt} - Photo ${currentIndex + 1}`}
          loading={priority && currentIndex === 0 ? "eager" : "lazy"}
          fetchPriority={priority && currentIndex === 0 ? "high" : "auto"}
          width={640}
          height={400}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 640px"
          onTinyImage={handleTinyImage}
          className={cn(
            "w-full h-full select-none pointer-events-none md:group-hover:scale-105 transition-transform duration-300",
            isAnimating && "md:scale-[1.02]"
          )}
        />
      </div>

      {hasMultipleImages && showArrows && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white text-foreground rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10 shadow-md backdrop-blur-sm"
            onClick={goToPrevious}
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white text-foreground rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10 shadow-md backdrop-blur-sm"
            onClick={goToNext}
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </>
      )}

      {hasMultipleImages && (
        <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 text-white text-xs font-medium rounded-md z-10">
          {currentIndex + 1} / {imageList.length}
        </div>
      )}

      {hasMultipleImages && showDots && imageList.length <= 6 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {imageList.map((_, index) => (
            <button
              key={index}
              onClick={(e) => goToIndex(index, e)}
              className={cn(
                "w-2 h-2 rounded-full transition-all shadow-sm",
                index === currentIndex 
                  ? "bg-white w-4" 
                  : "bg-white/60 hover:bg-white/80"
              )}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
